import { associateDesktopWorkspace } from '@/hooks/use-message-sync';
import { failureReason } from '@/lib/conversations/precondition-failure';
import type { ConversationScopes } from '@/lib/desktop-shell/conversation-scope';
import type { DesktopShellStore } from '@/lib/desktop-shell/store';
import { PERSONAL_WORKSPACE_KEY, WORKSPACE_KEY } from '@/lib/desktop-storage-keys';
import { openDirectoryDialog } from '@/lib/electron-bridge';
import { errorMessage } from '@/lib/error-message';
import { isLocalMachine } from '@/lib/local-runtime';
import {
  cloneNativeSourceControlRepository,
  ensureNativeManagedWorkspace,
} from '@/lib/native-desktop';
import { isCloudRepositoryWorkspace } from '@/lib/sidebar-threads';
import {
  checkoutSourceControlRepository,
  configureSourceControlRepositoryForWorkspace,
  resolveSourceControlRepository,
  type SourceControlRepository,
} from '@/lib/source-control-connector';

export interface RepositoryActionsOptions {
  store: DesktopShellStore;
  conversations: ConversationScopes;
  preview: boolean;
  selectedCloudRuntimeActive: boolean;
  resetConversation: (workspace?: string) => string;
  restoreProject: (project: string) => void;
  sidebarProjectKey: (path: string) => string;
}

/**
 * Owns repository selection and cloning across native and hosted runtimes. A folder chosen,
 * cloned or checked out for a conversation goes to that conversation, even when the user opened
 * another while it was being made (`ConversationScope`).
 */
export function createRepositoryActions({
  store,
  conversations,
  preview,
  selectedCloudRuntimeActive,
  resetConversation,
  restoreProject,
  sidebarProjectKey,
}: RepositoryActionsOptions) {
  function rememberProject(path: string): void {
    restoreProject(path);
    restoreProject(sidebarProjectKey(path));
  }

  async function persistWorkspace(
    path: string,
    name?: string,
    logicalWorkspaceId?: string,
  ): Promise<{ id: string; rootPath: string } | null> {
    const { setSyncedWorkspaces, setWorkspaceSyncError } = store.getState();
    try {
      const saved = await associateDesktopWorkspace(path, {
        ...(name === undefined ? {} : { name }),
        ...(logicalWorkspaceId === undefined ? {} : { logicalWorkspaceId }),
      });
      setSyncedWorkspaces((current) => [
        saved,
        ...current.filter((entry) => entry.id !== saved.id),
      ]);
      return saved;
    } catch (error) {
      setWorkspaceSyncError(errorMessage(error));
      return null;
    }
  }

  async function attachWorkspaceToConversation(input: {
    logicalWorkspaceId: string;
    path?: string;
    name?: string;
  }): Promise<boolean> {
    if (preview || !isLocalMachine() || selectedCloudRuntimeActive) return false;
    const conversation = conversations.shown();
    const path = input.path ?? (await openDirectoryDialog());
    if (path === null) return false;
    try {
      const saved = await persistWorkspace(path, input.name, input.logicalWorkspaceId);
      // The conversation now works in the located folder (its next turn records it there too).
      if (saved !== null) {
        store
          .getState()
          .setSyncedThreads((current) =>
            current.map((entry) =>
              entry.id === conversation.chatId
                ? { ...entry, workspaceId: saved.id, workspaceRoot: saved.rootPath }
                : entry,
            ),
          );
      }
      rememberProject(path);
      conversation.place({ workspace: saved?.rootPath ?? path, executionMode: 'local' });
      conversation.whileShown((state) => {
        state.setGitStatus(null);
        state.setWorkspaceProvisionError(null);
        state.setWorkspaceSyncError(null);
      });
      return true;
    } catch (error) {
      conversation.whileShown((state) => state.setWorkspaceProvisionError(errorMessage(error)));
      return false;
    }
  }

  async function chooseWorkspaceForHandoff(input: {
    logicalWorkspaceId: string;
    name?: string;
  }): Promise<string | null> {
    if (preview || !isLocalMachine()) return null;
    const path = await openDirectoryDialog();
    if (path === null) return null;
    const { setSyncedWorkspaces, setWorkspaceProvisionError } = store.getState();
    try {
      const saved = await associateDesktopWorkspace(path, {
        ...(input.name === undefined ? {} : { name: input.name }),
        logicalWorkspaceId: input.logicalWorkspaceId,
      });
      setSyncedWorkspaces((current) => [
        saved,
        ...current.filter((entry) => entry.id !== saved.id && entry.rootPath !== saved.rootPath),
      ]);
      rememberProject(saved.rootPath);
      setWorkspaceProvisionError(null);
      return saved.rootPath;
    } catch (error) {
      setWorkspaceProvisionError(errorMessage(error));
      return null;
    }
  }

  async function cloneWorkspaceForConversation(input: {
    logicalWorkspaceId: string;
    repository: SourceControlRepository;
    name?: string;
  }): Promise<boolean> {
    if (preview || !isLocalMachine() || selectedCloudRuntimeActive) return false;
    const destinationRoot = await openDirectoryDialog();
    if (destinationRoot === null) return false;
    try {
      const path = await cloneNativeSourceControlRepository({
        destinationRoot,
        cloneUrl: input.repository.cloneUrl,
      });
      return await attachWorkspaceToConversation({
        logicalWorkspaceId: input.logicalWorkspaceId,
        path,
        name: input.name,
      });
    } catch (error) {
      store.getState().setWorkspaceProvisionError(errorMessage(error));
      return false;
    }
  }

  async function startPersonalConversation(): Promise<boolean> {
    if (preview || !isLocalMachine() || selectedCloudRuntimeActive) return false;
    try {
      const { rootPath } = await ensureNativeManagedWorkspace({ kind: 'default' });
      const {
        setGitStatus,
        setGitStatusState,
        setPersonalWorkspaceRoot,
        setWorkspace,
        setWorkspaceProvisionError,
        setWorkspaceSyncError,
      } = store.getState();
      localStorage.setItem(PERSONAL_WORKSPACE_KEY, rootPath);
      localStorage.setItem(WORKSPACE_KEY, rootPath);
      setGitStatus(null);
      setGitStatusState('loading');
      setPersonalWorkspaceRoot(rootPath);
      setWorkspace(rootPath);
      setWorkspaceProvisionError(null);
      setWorkspaceSyncError(null);
      resetConversation(rootPath);
      await persistWorkspace(rootPath, 'Personal workspace');
      return true;
    } catch (error) {
      store.getState().setWorkspaceProvisionError(errorMessage(error));
      return false;
    }
  }

  async function selectWorkspace(): Promise<boolean> {
    if (preview || !isLocalMachine() || selectedCloudRuntimeActive) return false;
    const path = await openDirectoryDialog();
    if (path === null) return false;
    const { setWorkspace, setWorkspaceSyncError } = store.getState();
    rememberProject(path);
    localStorage.setItem(WORKSPACE_KEY, path);
    setWorkspace(path);
    resetConversation();
    setWorkspaceSyncError(null);
    await persistWorkspace(path);
    return true;
  }

  async function cloneSourceControlRepository(
    repository: SourceControlRepository,
  ): Promise<boolean> {
    if (preview) return false;
    const { setWorkspace, setWorkspaceSyncError } = store.getState();
    if (!isLocalMachine()) {
      const conversation = conversations.of(resetConversation());
      conversation.update({ cloudRepositoryBusy: true });
      try {
        const path = await checkoutSourceControlRepository(repository, conversation.chatId);
        rememberProject(path);
        conversation.place({ workspace: path });
        return true;
      } finally {
        conversation.update({ cloudRepositoryBusy: false });
      }
    }
    const destinationRoot = await openDirectoryDialog();
    if (destinationRoot === null) return false;
    const path = await cloneNativeSourceControlRepository({
      destinationRoot,
      cloneUrl: repository.cloneUrl,
    });
    rememberProject(path);
    localStorage.setItem(WORKSPACE_KEY, path);
    setWorkspace(path);
    resetConversation(path);
    setWorkspaceSyncError(null);
    await persistWorkspace(path);
    return true;
  }

  function startProjectConversation(path: string | null): void {
    if (path === null) return;
    // A repository only the cloud has checked out: its conversation runs there, on a machine too.
    const cloudOnly = isCloudRepositoryWorkspace(path);
    if (cloudOnly) store.getState().setExecutionMode('cloud');
    else localStorage.setItem(WORKSPACE_KEY, path);
    const nextId = resetConversation(path);
    if (!cloudOnly) return;
    const conversation = conversations.of(nextId);
    conversation.update({ cloudRepositoryBusy: true });
    store.getState().setCloudProvisionError(null);
    void configureSourceControlRepositoryForWorkspace(path, nextId)
      .then((workspaceRoot) => conversation.place({ workspace: workspaceRoot }))
      .catch((error: unknown) => {
        conversation.place({ workspace: '/workspace' });
        conversation.whileShown((state) => {
          state.setCloudProvisionError(errorMessage(error));
          // The cloud has no copy of it: the conversation offers its ways on (its folder here…).
          if (failureReason(error) === 'cloud_project_missing') state.setCloudProjectMissing(path);
        });
      })
      .finally(() => conversation.update({ cloudRepositoryBusy: false }));
  }

  /**
   * A forge repository only the cloud has gets a folder of this computer: one the user already
   * has (`folder`) or a clone (`clone`). The folder joins its project, so the repository is one
   * entry with both copies, and a new conversation starts in it, on this computer.
   */
  async function linkRepositoryLocally(input: {
    logicalWorkspaceId: string;
    name: string;
    forge: { fullName: string; remote: string | null };
    how: 'folder' | 'clone';
  }): Promise<boolean> {
    if (preview || !isLocalMachine()) return false;
    const { logicalWorkspaceId, name } = input;
    let path: string | null;
    if (input.how === 'folder') {
      path = await chooseWorkspaceForHandoff({ logicalWorkspaceId, name });
    } else {
      const destinationRoot = await openDirectoryDialog();
      if (destinationRoot === null) return false;
      try {
        const repository = await resolveSourceControlRepository(
          input.forge.remote ?? input.forge.fullName,
        );
        const cloned = await cloneNativeSourceControlRepository({
          destinationRoot,
          cloneUrl: repository.cloneUrl,
        });
        path = (await persistWorkspace(cloned, name, logicalWorkspaceId))?.rootPath ?? null;
      } catch (error) {
        store.getState().setWorkspaceProvisionError(errorMessage(error));
        return false;
      }
    }
    if (path === null) return false;
    rememberProject(path);
    startProjectConversation(path);
    return true;
  }

  return {
    attachWorkspaceToConversation,
    chooseWorkspaceForHandoff,
    cloneSourceControlRepository,
    cloneWorkspaceForConversation,
    linkRepositoryLocally,
    selectWorkspace,
    startPersonalConversation,
    startProjectConversation,
  };
}
